import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import './MyProfile-hybrid.scss';
import { useNavigate } from "react-router-dom";
import { useRequiredProfile } from "../../hooks/useProfileData";
import { ThemeAppearanceSelect } from "../ThemeAppearanceSelect";
import { useToast } from "../../Contexts/ToastContext";
import { STORAGE_KEYS, clearAuthStorage } from "../../utils/storage";
import axios from "axios";
import { LinkedAccountsModal } from "../LinkedAccountsModal";
import { NotificationSettingsSection } from "../NotificationSettingsSection";
import { ContactVerifyModal, getProfileContactChanges } from "../ContactVerifyModal";
import { ForgotPasswordModal } from "../../Auth/ForgotPasswordModal";

function getForgotPasswordTarget(userData = {}) {
    const phone = String(userData.phone || '').replace(/\D/g, '').slice(-10);
    if (phone.length === 10) return { channel: 'phone', identifier: phone };
    const email = String(userData.email || '').trim();
    if (email.includes('@')) return { channel: 'email', identifier: email };
    return null;
}

export function MobMyProfileHybrid() {
    const navigate = useNavigate();
    const isGuest = typeof window !== 'undefined' && localStorage.getItem('guestUser') === 'true';
    const [activeTab, setActiveTab] = useState(isGuest ? 'settings' : 'profile');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [showLinkedAccountsModal, setShowLinkedAccountsModal] = useState(false);
    const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);
    const [changePasswordMessage, setChangePasswordMessage] = useState('');
    const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState('');
    const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
    const [deleteAccountMessage, setDeleteAccountMessage] = useState('');
    const [contactRequest, setContactRequest] = useState(null);
    const [contactQueue, setContactQueue] = useState([]);
    const { profileData, loading, error, fetchProfile, updateProfileData, clearProfile } = useRequiredProfile();
    const { showToast } = useToast();
    const firstNameInputRef = useRef(null);
    const pendingFirstNameFocusRef = useRef(false);

    const focusFirstNameInput = useCallback(() => {
        const input = firstNameInputRef.current;
        if (!input) return false;
        try {
            input.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
            input.focus({ preventScroll: true });
            const end = input.value?.length ?? 0;
            if (typeof input.setSelectionRange === "function") {
                input.setSelectionRange(end, end);
            }
            return document.activeElement === input;
        } catch (_err) {
            try {
                input.focus();
                return document.activeElement === input;
            } catch (_err2) {
                return false;
            }
        }
    }, []);

    const attachFirstNameInputRef = useCallback(
        (node) => {
            firstNameInputRef.current = node;
            if (!node || !pendingFirstNameFocusRef.current) return;
            pendingFirstNameFocusRef.current = false;
            focusFirstNameInput();
        },
        [focusFirstNameInput]
    );

    // Form data state
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        pincode: ""
    });

    // Initialize form data when profile loads
    useEffect(() => {
        if (profileData?.data) {
            const userData = profileData.data;
            setFormData({
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                phone: userData.phone || "",
                email: userData.email || "",
                pincode: userData.pincode || ""
            });
        }
    }, [profileData]);

    useLayoutEffect(() => {
        if (!isEditing || isGuest) return;
        focusFirstNameInput();
    }, [isEditing, isGuest, focusFirstNameInput]);

    const menuItems = isGuest
        ? [{ id: 'settings', icon: 'bi-gear', label: 'Settings' }]
        : [
            { id: 'profile', icon: 'bi-person', label: 'Profile Details' },
            { id: 'settings', icon: 'bi-gear', label: 'Settings' },
        ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value
        }));
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data to original values
            if (profileData?.data) {
                const userData = profileData.data;
                setFormData({
                    firstName: userData.firstName || "",
                    lastName: userData.lastName || "",
                    phone: userData.phone || "",
                    email: userData.email || "",
                    pincode: userData.pincode || ""
                });
            }
        } else {
            // iOS Safari only allows focus inside the same tap — mount input then focus immediately.
            pendingFirstNameFocusRef.current = true;
            flushSync(() => setIsEditing(true));
            focusFirstNameInput();
            return;
        }
        setIsEditing(false);
    };

    const handleSave = async () => {
        const contactPlan = getProfileContactChanges(profileData?.data, formData);
        if (contactPlan.error) {
            showToast({ message: contactPlan.error });
            return;
        }
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            const profilePayload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
            };
            if (/^\d{6}$/.test(String(formData.pincode || ''))) {
                profilePayload.pincode = String(formData.pincode);
            }
            await axios.put('/users/profile', profilePayload, config);

            if (contactPlan.changes.length) {
                const [first, ...rest] = contactPlan.changes;
                setContactQueue(rest);
                setContactRequest(first);
                setIsEditing(false);
                return;
            }
            await fetchProfile();
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            const msg = error.response?.data?.msg || error.response?.data?.message;
            showToast({ message: msg || 'Failed to update profile. Please try again.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleContactVerified = async (payload) => {
        const label = payload.field === 'phone'
            ? (payload.removed ? 'Mobile number removed.' : 'Mobile number updated.')
            : (payload.removed ? 'Email removed.' : 'Email updated.');
        if (contactQueue.length) {
            const [next, ...rest] = contactQueue;
            setContactQueue(rest);
            setContactRequest(next);
            showToast({ message: label });
            return;
        }
        setContactRequest(null);
        setContactQueue([]);
        await fetchProfile();
        showToast({ message: label });
    };

    const handleContactCancel = async () => {
        setContactRequest(null);
        setContactQueue([]);
        await fetchProfile();
        showToast({ message: 'Contact change cancelled.' });
    };

    const openChangePasswordModal = () => setShowChangePasswordModal(true);
    const closeChangePasswordModal = () => {
        setShowChangePasswordModal(false);
        setChangePasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
        setChangePasswordMessage('');
    };

    const openForgotPasswordModal = () => {
        const target = getForgotPasswordTarget(profileData?.data);
        if (!target) {
            showToast({ message: 'Add a mobile number or email on your profile first.' });
            return;
        }
        setShowForgotPasswordModal(true);
    };

    const handleChangePasswordSubmit = async (e) => {
        e.preventDefault();
        const hasExistingPassword = profileData?.data?.passwordSet === true;
        const { currentPassword, newPassword, confirmNewPassword } = changePasswordForm;
        if (hasExistingPassword && !currentPassword.trim()) {
            setChangePasswordMessage('Current password is required.');
            return;
        }
        if (!newPassword.trim()) {
            setChangePasswordMessage('New password is required.');
            return;
        }
        if (newPassword.length < 6) {
            setChangePasswordMessage('New password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setChangePasswordMessage('New password and confirmation do not match.');
            return;
        }
        setChangePasswordLoading(true);
        setChangePasswordMessage('');
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            if (hasExistingPassword) {
                await axios.patch('/users/change-password', {
                    currentPassword,
                    newPassword
                }, { headers });
                closeChangePasswordModal();
                clearAuthStorage();
                navigate('/auth/login');
                return;
            }
            await axios.put('/users/set-password', {
                password: newPassword,
                confirmPassword: confirmNewPassword
            }, { headers });
            localStorage.setItem('passwordSet', 'true');
            updateProfileData({ data: { passwordSet: true } });
            await fetchProfile();
            closeChangePasswordModal();
            showToast({ message: 'Password set.' });
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.msg || 'Failed to update password. Please try again.';
            setChangePasswordMessage(msg);
        } finally {
            setChangePasswordLoading(false);
        }
    };

    const openDeleteAccountModal = () => {
        setShowDeleteAccountModal(true);
        setDeleteAccountConfirmText('');
        setDeleteAccountMessage('');
    };
    const closeDeleteAccountModal = () => {
        setShowDeleteAccountModal(false);
        setDeleteAccountConfirmText('');
        setDeleteAccountMessage('');
    };

    const handleDeleteAccountConfirm = async () => {
        if (deleteAccountConfirmText.trim().toUpperCase() !== 'DELETE') {
            setDeleteAccountMessage('Please type DELETE to confirm.');
            return;
        }
        setDeleteAccountLoading(true);
        setDeleteAccountMessage('');
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setDeleteAccountMessage('You are not logged in.');
                return;
            }
            await axios.delete('/users/account', {
                headers: { Authorization: `Bearer ${token}` },
            });
            closeDeleteAccountModal();
            clearProfile();
            clearAuthStorage();
            showToast({ message: 'Your account has been deleted.' });
            navigate('/auth/login', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.msg || 'Failed to delete account. Please try again.';
            setDeleteAccountMessage(msg);
        } finally {
            setDeleteAccountLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="hybrid-profile-container">
                <div className="profile-page">
                    <div className="loading-container">
                        <div
                            className="loading-spinner"
                            role="status"
                            aria-label="Loading profile"
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (!isGuest && error) {
        return (
            <div className="hybrid-profile-container">
                <div className="profile-page">
                    <div className="error-container">
                        <div className="error-message">Error loading profile. Please try again.</div>
                        <button onClick={() => window.location.reload()}>Retry</button>
                    </div>
                </div>
            </div>
        );
    }

    const userData = profileData?.data || {};
    const firstName = isGuest ? (localStorage.getItem('guestFirstName') || '') : (userData.firstName || '');
    const lastName = isGuest ? (localStorage.getItem('guestLastName') || '') : (userData.lastName || '');
    const guestWishlist = isGuest ? JSON.parse(localStorage.getItem('guestWishlist') || '[]') : [];
    const guestOrderCount = isGuest ? parseInt(localStorage.getItem(STORAGE_KEYS.guestOrderCount) || '0', 10) : 0;
    const {
        phone,
        email,
        pincode,
        isVerified,
        ordersCount = 0,
        wishlistCount = 0,
        reviewsCount = 0,
    } = userData;
    const passwordSet = userData.passwordSet === true;
    const forgotPasswordTarget = getForgotPasswordTarget(userData);

    return (
        <div className="hybrid-profile-container">
            <div className="profile-page">
                <div className="profile-header">
                    <div className="header-content">
                        <div className="profile-avatar">
                            <div className="avatar-circle">
                                <i className="bi bi-person-fill"></i>
                            </div>
                            <div className="status-indicator online"></div>
                        </div>
                        
                        <div className="profile-info">
                            <h1 className="profile-name">{firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'User'}</h1>
                            <p className="profile-role">{isGuest ? 'Guest' : 'Premium Member'}</p>
                            <div className="profile-stats">
                                <div className="stat" onClick={() => navigate('/orders')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/orders')}>
                                    <span className="stat-number">{isGuest ? guestOrderCount : ordersCount}</span>
                                    <span className="stat-label">Orders</span>
                                </div>
                                <div className="stat" onClick={() => navigate('/wishlist')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/wishlist')}>
                                    <span className="stat-number">{isGuest ? guestWishlist.length : wishlistCount}</span>
                                    <span className="stat-label">Wishlist</span>
                                </div>
                                {!isGuest && (
                                <div className="stat">
                                    <span className="stat-number">{reviewsCount}</span>
                                    <span className="stat-label">Reviews</span>
                                </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mobile-tab-nav">
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            className={`tab-item ${activeTab === item.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(item.id)}
                        >
                            <i className={`bi ${item.icon} tab-icon`}></i>
                            <span className="tab-label">{item.label}</span>
                        </button>
                    ))}
                </div>

                <div className="main-content">
                    {!isGuest && activeTab === 'profile' && (
                    <div className={`profile-card${isEditing ? ' profile-card--editing' : ''}`}>
                        <div className="profile-content">
                            <div className="profile-info-section">
                                {/* <h3>About</h3> */}
                                <div className="info-grid">
                                    <div className="info-item">
                                        <i className="bi bi-person"></i>
                                        <div>
                                            <span className="info-label">First Name</span>
                                            {!isEditing ? (
                                                <span className={`info-value${!firstName ? ' not-added' : ''}`}>{firstName || 'Not added'}</span>
                                            ) : (
                                                <input
                                                    ref={attachFirstNameInputRef}
                                                    type="text"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    className="info-input"
                                                    placeholder="Not added"
                                                    autoComplete="given-name"
                                                    autoFocus
                                                    inputMode="text"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="bi bi-person"></i>
                                        <div>
                                            <span className="info-label">Last Name</span>
                                            {!isEditing ? (
                                                <span className={`info-value${!lastName ? ' not-added' : ''}`}>{lastName || 'Not added'}</span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    className="info-input"
                                                    placeholder="Not added"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="bi bi-telephone"></i>
                                        <div>
                                            <span className="info-label">Phone</span>
                                            {!isEditing ? (
                                                <span className={`info-value${!phone ? ' not-added' : ''}`}>{phone || 'Not added'}</span>
                                            ) : (
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className="info-input"
                                                    placeholder="Not added"
                                                    maxLength="10"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="bi bi-envelope"></i>
                                        <div>
                                            <span className="info-label">Email</span>
                                            {!isEditing ? (
                                                <span className={`info-value${!email ? ' not-added' : ''}`}>{email || 'Not added'}</span>
                                            ) : (
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="info-input"
                                                    placeholder="Not added"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="bi bi-geo-alt"></i>
                                        <div>
                                            <span className="info-label">Pincode</span>
                                            {!isEditing ? (
                                                <span className={`info-value${!pincode ? ' not-added' : ''}`}>{pincode || 'Not added'}</span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    name="pincode"
                                                    value={formData.pincode}
                                                    onChange={handleInputChange}
                                                    className="info-input"
                                                    placeholder="Not added"
                                                    maxLength="6"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="bi bi-shield-check"></i>
                                        <div>
                                            <span className="info-label">Verification Status</span>
                                            <span className="info-value">{isVerified ? 'Verified' : 'Not Verified'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isEditing && (
                                <p className="profile-contact-hint">
                                    Adding a mobile number or email sends a code to that new contact. Changing one that is already saved also sends a code to the current contact first.
                                </p>
                            )}

                            <div className="profile-actions">
                                {!isEditing ? (
                                    <>
                                        <button className="action-btn primary" onClick={handleEditToggle}>
                                            <i className="bi bi-pencil"></i>
                                            <span>Edit Profile</span>
                                        </button>
                                        <button className="action-btn secondary" onClick={() => navigate("/orders")}>
                                            <i className="bi bi-bag"></i>
                                            <span>View Orders</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className="action-btn primary" 
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        >
                                            <i className="bi bi-check-circle"></i>
                                            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                                        </button>
                                        <button 
                                            className="action-btn secondary" 
                                            onClick={handleEditToggle}
                                            disabled={isSaving}
                                        >
                                            <i className="bi bi-x-circle"></i>
                                            <span>Cancel</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {activeTab === 'settings' && (
                    <div className="profile-card settings-card">
                        <div className="profile-content">
                            {/* <h3>Settings</h3> */}

                            <div className="settings-section">
                                <h4>General</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-globe"></i> Language</span>
                                    <span className="settings-value">English</span>
                                </div>
                                <ThemeAppearanceSelect />
                            </div>

                            <NotificationSettingsSection
                                profileData={profileData}
                                isGuest={isGuest}
                                onSaved={() => fetchProfile(true)}
                            />

                            {!isGuest && (
                            <div className="settings-section">
                                <h4>Account</h4>
                                <div className="settings-row clickable" onClick={openChangePasswordModal}>
                                    <span><i className="bi bi-key"></i> {passwordSet ? 'Change password' : 'Set password'}</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable" onClick={openForgotPasswordModal}>
                                    <span><i className="bi bi-unlock"></i> Forgot password</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable" onClick={openDeleteAccountModal}>
                                    <span><i className="bi bi-trash"></i> Delete account</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable" onClick={() => setShowLinkedAccountsModal(true)}>
                                    <span><i className="bi bi-link-45deg"></i> Linked accounts (Google, Facebook, Apple)</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                            </div>
                            )}

                            <div className="settings-section">
                                <h4>Privacy</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-database"></i> Data privacy &amp; usage</span>
                                    <span className="settings-value">Manage</span>
                                </div>
                                <div className="settings-row">
                                    <span><i className="bi bi-megaphone"></i> Ad personalization</span>
                                    <span className="settings-value">Opt-in</span>
                                </div>
                            </div>

                            {!isGuest && (
                            <>
                            <div className="settings-section">
                                <h4>Login &amp; access</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-shield-lock"></i> Two-factor authentication (2FA)</span>
                                    <span className="settings-value">Off</span>
                                </div>
                                <div className="settings-row clickable">
                                    <span><i className="bi bi-clock-history"></i> Recent login activity</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable">
                                    <span><i className="bi bi-phone"></i> Manage devices / sessions</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                            </div>

                            <div className="settings-section">
                                <h4>Password</h4>
                                <div className="settings-row clickable" onClick={openChangePasswordModal}>
                                    <span><i className="bi bi-key"></i> {passwordSet ? 'Change password' : 'Set password'}</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable" onClick={openForgotPasswordModal}>
                                    <span><i className="bi bi-unlock"></i> Forgot password</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                {passwordSet && (
                                <div className="settings-row">
                                    <span><i className="bi bi-calendar-check"></i> Last password change</span>
                                    <span className="settings-value">—</span>
                                </div>
                                )}
                            </div>

                            <div className="settings-section">
                                <h4>Security alerts</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-bell"></i> Alerts (suspicious login, password change)</span>
                                    <span className="settings-value">Email</span>
                                </div>
                            </div>

                            <div className="settings-section">
                                <h4>Connected apps</h4>
                                <div className="settings-row clickable">
                                    <span><i className="bi bi-app"></i> Third-party access</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                            </div>
                            </>
                            )}

                            <div className="settings-section">
                                <h4>Legal &amp; About</h4>
                                <div className="settings-row clickable" onClick={() => navigate('/terms')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/terms')}>
                                    <span><i className="bi bi-file-text"></i> Terms of Service</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row clickable" onClick={() => navigate('/privacy')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/privacy')}>
                                    <span><i className="bi bi-shield-check"></i> Privacy Policy</span>
                                    <i className="bi bi-chevron-right"></i>
                                </div>
                                <div className="settings-row">
                                    <span><i className="bi bi-info-circle"></i> App version</span>
                                    <span className="settings-value">1.0.0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    <LinkedAccountsModal
                        isOpen={showLinkedAccountsModal}
                        onClose={() => setShowLinkedAccountsModal(false)}
                        loginType={userData?.loginType}
                    />

                    {contactRequest && (
                        <ContactVerifyModal
                            key={`${contactRequest.field}:${contactRequest.value}`}
                            field={contactRequest.field}
                            value={contactRequest.value}
                            onClose={handleContactCancel}
                            onVerified={handleContactVerified}
                        />
                    )}
                    {showForgotPasswordModal && forgotPasswordTarget && (
                        <ForgotPasswordModal
                            channel={forgotPasswordTarget.channel}
                            identifier={forgotPasswordTarget.identifier}
                            onClose={() => setShowForgotPasswordModal(false)}
                            onSuccess={(message) => {
                                setShowForgotPasswordModal(false);
                                showToast({ message });
                            }}
                        />
                    )}
                    {showChangePasswordModal && (
                        <div className="profile-modal-overlay" onClick={() => !changePasswordLoading && closeChangePasswordModal()}>
                            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="profile-modal-header">
                                    <h3>{passwordSet ? 'Change password' : 'Set password'}</h3>
                                    <button type="button" className="profile-modal-close" onClick={closeChangePasswordModal} disabled={changePasswordLoading} aria-label="Close">×</button>
                                </div>
                                <form onSubmit={handleChangePasswordSubmit} className="profile-modal-body">
                                    {passwordSet && (
                                    <div className="profile-modal-field">
                                        <label htmlFor="mob-profile-current-password">Current password</label>
                                        <input
                                            id="mob-profile-current-password"
                                            type="password"
                                            value={changePasswordForm.currentPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, currentPassword: e.target.value }))}
                                            placeholder="Enter current password"
                                            autoComplete="current-password"
                                        />
                                    </div>
                                    )}
                                    <div className="profile-modal-field">
                                        <label htmlFor="mob-profile-new-password">{passwordSet ? 'New password' : 'Password'}</label>
                                        <input
                                            id="mob-profile-new-password"
                                            type="password"
                                            value={changePasswordForm.newPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
                                            placeholder="At least 6 characters"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <div className="profile-modal-field">
                                        <label htmlFor="mob-profile-confirm-password">{passwordSet ? 'Confirm new password' : 'Confirm password'}</label>
                                        <input
                                            id="mob-profile-confirm-password"
                                            type="password"
                                            value={changePasswordForm.confirmNewPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, confirmNewPassword: e.target.value }))}
                                            placeholder={passwordSet ? 'Confirm new password' : 'Confirm password'}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {changePasswordMessage && <div className="profile-modal-message profile-modal-message--error">{changePasswordMessage}</div>}
                                    <div className="profile-modal-footer">
                                        <button type="button" className="profile-modal-btn-secondary" onClick={closeChangePasswordModal} disabled={changePasswordLoading}>Cancel</button>
                                        <button type="submit" className="profile-modal-btn-primary" disabled={changePasswordLoading}>{changePasswordLoading ? 'Updating...' : (passwordSet ? 'Change password' : 'Set password')}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {showDeleteAccountModal && (
                        <div className="profile-modal-overlay" onClick={() => !deleteAccountLoading && closeDeleteAccountModal()}>
                            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="profile-modal-header">
                                    <h3>Delete account</h3>
                                    <button type="button" className="profile-modal-close" onClick={closeDeleteAccountModal} disabled={deleteAccountLoading} aria-label="Close">×</button>
                                </div>
                                <div className="profile-modal-body">
                                    <p className="profile-modal-warning">This action cannot be undone. All your data will be permanently removed. Type <strong>DELETE</strong> below to confirm.</p>
                                    <div className="profile-modal-field">
                                        <input
                                            type="text"
                                            value={deleteAccountConfirmText}
                                            onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                                            placeholder="Type DELETE to confirm"
                                            className={deleteAccountMessage ? 'profile-modal-input-error' : ''}
                                        />
                                    </div>
                                    {deleteAccountMessage && <div className="profile-modal-message profile-modal-message--error">{deleteAccountMessage}</div>}
                                    <div className="profile-modal-footer">
                                        <button type="button" className="profile-modal-btn-secondary" onClick={closeDeleteAccountModal} disabled={deleteAccountLoading}>Cancel</button>
                                        <button type="button" className="profile-modal-btn-danger" onClick={handleDeleteAccountConfirm} disabled={deleteAccountLoading || deleteAccountConfirmText.trim().toUpperCase() !== 'DELETE'}>
                                            {deleteAccountLoading ? 'Deleting...' : 'Delete my account'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
